import api from 'api';
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {Note, RequestDetails, NoteSort} from 'types';
import {findLast} from 'lodash';
import {getFullName} from 'helpers/user';
import {formatISO} from 'date-fns';
import useCurrentUser from 'hooks/useCurrentUser';
import {useMemo, useState} from 'react';

async function getRequestById({
  queryKey,
}: QueryFunctionContext): Promise<RequestDetails | undefined> {
  const [, id, order, status] = queryKey;
  const {data} = await api.get<RequestDetails>(
    `/requests/${id}/?is_sequential_order_number=true`,
    {
      params: {order, status},
    }
  );
  return data;
}

export function useActivityNotes(orderId: string) {
  const [timelineSort, setTimelineSort] = useState<NoteSort | 'all'>('all');
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const queryCache = queryClient.getQueryCache().getAll();

  const [, , order, status] =
    (findLast(queryCache, (q) => q.queryKey[0] === 'requests')
      ?.queryKey as string[]) || [];

  const {data, isLoading, refetch} = useQuery(
    ['request_details', orderId, order, status],
    getRequestById,
    {enabled: !!orderId}
  );
  const createNote = useMutation(
    'create_note',
    async ({
      text,
      driver,
    }: {
      text: string;
      driver: boolean;
    }): Promise<string> => {
      const {data: res} = await api.post<{
        text: string;
        is_visible_to_drivers: boolean;
      }>(`/requests/${data?.id}/note/`, {
        text,
        is_visible_to_drivers: driver,
      });
      return res.text;
    },
    {
      onMutate: async ({text, driver}) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(['request_details', orderId]);

        // Snapshot the previous value
        const previousRequest = queryClient.getQueryData<RequestDetails>([
          'request_details',
          orderId,
        ]);

        const newNote: Note = {
          id: Date.now(),
          text,
          is_visible_to_drivers: driver,
          author: getFullName(currentUser),
          avatar: currentUser.avatar,
          created_at: formatISO(Date.now()),
        };

        const nextRequest: RequestDetails | undefined = previousRequest
          ? {
              ...previousRequest,
              notes: [newNote, ...previousRequest.notes],
            }
          : undefined;

        // Optimistically update to the new value
        queryClient.setQueryData(['request_details', orderId], nextRequest);

        // Return a context object with the snapshotted value
        return {previousRequest};
      },

      // If the mutation fails, use the context returned from onMutate to roll back
      onError: (err, _text, context?: {previousRequest?: RequestDetails}) => {
        queryClient.setQueryData(
          ['request_details', orderId],
          context?.previousRequest
        );
      },

      // Always refetch after error or success:
      onSettled: () => {
        queryClient.invalidateQueries(['request_details', orderId]);
      },
    }
  );

  function onSubmitNote(text: string, driver: boolean) {
    return createNote.mutate({text, driver});
  }

  const notes = useMemo(() => {
    return data?.notes?.filter((item) => {
      if (timelineSort === 'all') {
        return true;
      }
      return item.note_type === timelineSort;
    });
  }, [data?.notes, timelineSort]);
  return {
    isLoading,
    refetch,
    onSubmitNote,
    timelineSort,
    setTimelineSort,
    notes,
  };
}
